import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

import { DataTable, type Column } from '@/components/ui/DataTable';
import { formatCount } from '@/lib/numbers';

import {
  tableRows,
  type DashboardData,
  type MetricDefinition,
  type MetricTableId,
} from './dashboardData';

interface MetricTableProps {
  table: MetricTableId;
  caption: string;
  /** Absent while loading or after a failure. */
  metrics?: DashboardData['metrics'];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

/** "412 / 173" for a paired row; the plain count otherwise. */
function metricValue(row: MetricDefinition, metrics: DashboardData['metrics']): string {
  const first = formatCount(metrics[row.id].current);
  return row.pairedWith
    ? `${first} / ${formatCount(metrics[row.pairedWith].current)}`
    : first;
}

/**
 * One of the dashboard's two tables: fixed metric rows, a count each.
 *
 * While loading, the rows are drawn with a placeholder in place of each
 * number, rather than with the table's own loading panel. The rows are known
 * before the numbers are, and a panel of a different height would make the
 * page jump when they arrive.
 */
export function MetricTable({
  table,
  caption,
  metrics,
  loading = false,
  error,
  onRetry,
}: MetricTableProps) {
  const columns: Column<MetricDefinition>[] = [
    { key: 'metric', header: 'Metric', render: (row) => row.label },
    {
      key: 'count',
      header: 'Count',
      align: 'right',
      render: (row) => (
        // One width and one line in every state. A paired value is wider than
        // a placeholder, and without this the column widened when the numbers
        // arrived, a label wrapped, and the page grew by a row.
        <Box
          component="span"
          sx={{ display: 'inline-block', minWidth: '6.5em', whiteSpace: 'nowrap' }}
        >
          {loading || !metrics ? (
            <Skeleton width={56} sx={{ ml: 'auto' }} />
          ) : (
            metricValue(row, metrics)
          )}
        </Box>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={tableRows(table)}
      getRowKey={(row) => row.id}
      caption={caption}
      error={error}
      onRetry={onRetry}
    />
  );
}
